import {
  type GameState,
  type GameStatus,
  type Pos,
  isPathComplete,
} from '@color-dots/game';
import type { GearSoundEvent } from './gearSound.js';

export interface GearSoundTracker {
  prevPaths: Map<string, Pos[]>;
  prevPairComplete: Map<string, boolean>;
  prevStatus: GameStatus;
}

export function createGearSoundTracker(): GearSoundTracker {
  return {
    prevPaths: new Map(),
    prevPairComplete: new Map(),
    prevStatus: 'playing',
  };
}

export function resetGearSoundTracker(tracker: GearSoundTracker): void {
  tracker.prevPaths.clear();
  tracker.prevPairComplete.clear();
  tracker.prevStatus = 'playing';
}

/**
 * Detect gear SFX events from game-state delta (§11).
 * Call once per sync/render when visualStyle is gear.
 */
export function collectGearSoundEvents(
  state: GameState,
  tracker: GearSoundTracker,
): GearSoundEvent[] {
  const events: GearSoundEvent[] = [];

  for (const [color, path] of state.paths) {
    const prev = tracker.prevPaths.get(color) ?? [];
    if (path.length > prev.length && path.length >= 2) {
      events.push({ kind: 'extend', pathLength: path.length });
      break;
    }
    if (path.length < prev.length && prev.length >= 2) {
      events.push({ kind: 'backtrack', pathLength: prev.length });
      break;
    }
  }

  for (const [color, endpoints] of state.endpoints) {
    const complete = isPathComplete(state.paths.get(color), endpoints);
    const wasComplete = tracker.prevPairComplete.get(color) ?? false;
    if (complete && !wasComplete && state.status !== 'won') {
      events.push({ kind: 'pairComplete' });
    }
    tracker.prevPairComplete.set(color, complete);
  }

  if (state.status === 'won' && tracker.prevStatus !== 'won') {
    events.push({ kind: 'win' });
  }

  snapshotGearSoundTracker(state, tracker);
  return events;
}

function snapshotGearSoundTracker(state: GameState, tracker: GearSoundTracker): void {
  tracker.prevPaths = new Map(
    [...state.paths.entries()].map(([color, path]) => [color, [...path]]),
  );
  tracker.prevStatus = state.status;
}
